// React
import React, { ChangeEvent } from 'react';

// Components
import InputGroupWrapper from '../InputGroup';

// Translations
import { useIntl } from 'react-intl';
import messages from './messages';

// Styles
import clsx from 'clsx';

/**
 * A component which returns three fields for entering a birthdate
 * - Leading 0 for day and month value
 * - Type date smoothly in a row automatically jump to next fields
 * - Overwrite the preExisting value when some values are already in the input fields
 */

const BirthdayPicker = ({
  autoComplete,
  name,
  disabled,
  ...props
}: {
  autoComplete: 'off' | 'on';
  name: string;
  disabled?: string;
  form: any;
  field: any;
}) => {
  const { formatMessage } = useIntl();
  const { form, field } = props;
  const defaultStyles = clsx(
    'form-input',
    'w-full z-0',
    'placeholder-gray-300',
    {
      ['bg-gray-100 text-gray-300 border hover:border-gray-200']: disabled,
      'form-input-error': props?.hasError,
    }
  );

  // Define the prefixed keys for the inputs
  const yearKey = `${name}-year`;
  const monthKey = `${name}-month`;
  const dayKey = `${name}-day`;

  function handleOnFocusOut(): void {
    // Checking length of the value
    // e.g. "1990-05-19" Should always have length === 10
    // Then set "touched" to true, to ensure all 3 inputs have been filled
    if (field?.value && field?.value.length === 10) {
      form?.setFieldTouched(name);
    }

    form?.setFieldValue(name, field?.value?.replace(/-(\d{1}(?!\d))/g, '-0$1'));
  }

  function handleOnFocusCaptured(event: React.FocusEvent<HTMLInputElement>) {
    event.target.select();
  }

  function handleChangeDateValue(event: ChangeEvent<HTMLInputElement>): void {
    const { name: inputName, value } = event.target;
    // Split the whole date field value to a nice splitted object with custom keys
    const dateValues = {
      [yearKey]: field?.value ? field?.value?.split('-')[0] : '',
      [monthKey]: field?.value ? field?.value?.split('-')[1] : '',
      [dayKey]: field?.value ? field?.value?.split('-')[2] : '',
    };
    dateValues[inputName] = value;
    form?.setFieldValue(
      name,
      `${dateValues[yearKey]}-${dateValues[monthKey]}-${dateValues[dayKey]}`
    );
  }

  function handleOnKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
    event.preventDefault();
    const { value, maxLength, form: eventForm } = event.currentTarget;
    if (value.slice(0, maxLength).length === maxLength) {
      if (
        form &&
        event.key !== 'Tab' &&
        window.getSelection()?.toString() === ''
      ) {
        const index = [...eventForm].indexOf(event.currentTarget);
        eventForm?.elements[index + 1].focus();
      }
    }
  }
  return (
    <div className="flex flex-row">
      <input
        type="text"
        inputMode="numeric"
        value={field?.value?.split('-')[2] || ''}
        key={dayKey}
        aria-label={dayKey}
        name={dayKey}
        aria-invalid={props?.meta?.error}
        className={clsx(defaultStyles, 'mr-10')}
        placeholder={formatMessage(messages.dayPlaceholder)}
        maxLength={2}
        onChange={handleChangeDateValue}
        onFocusCapture={handleOnFocusCaptured}
        onBlur={handleOnFocusOut}
        onKeyUp={handleOnKeyUp}
        autoComplete={autoComplete}
      />
      <input
        type="text"
        inputMode="numeric"
        value={field?.value?.split('-')[1] || ''}
        key={monthKey}
        aria-label={monthKey}
        aria-invalid={props?.meta?.error}
        name={monthKey}
        className={clsx(defaultStyles, 'mr-10')}
        placeholder={formatMessage(messages.monthPlaceholder)}
        maxLength={2}
        onChange={handleChangeDateValue}
        onFocusCapture={handleOnFocusCaptured}
        onBlur={handleOnFocusOut}
        onKeyUp={handleOnKeyUp}
        autoComplete={autoComplete}
      />
      <input
        type="text"
        inputMode="numeric"
        value={field?.value?.split('-')[0] || ''}
        key={yearKey}
        aria-label={yearKey}
        name={yearKey}
        aria-invalid={props?.meta?.error}
        className={clsx(defaultStyles, 'm-0')}
        placeholder={formatMessage(messages.yearPlaceholder)}
        maxLength={4}
        onChange={handleChangeDateValue}
        onFocusCapture={handleOnFocusCaptured}
        onBlur={handleOnFocusOut}
        autoComplete={autoComplete}
      />
    </div>
  );
};

export { BirthdayPicker };

// eslint-disable-next-line react/no-multi-comp
const wrappedDatePicker = props => (
  <InputGroupWrapper {...props} component={BirthdayPicker} />
);

export default wrappedDatePicker;
